// WeatherService — composite weather provider; fetches from multiple providers and merges by priority

import { NoaaMetarProvider } from './NoaaMetarProvider.js';
import { MetServiceSynopProvider } from './MetServiceSynopProvider.js';
import { MetServiceArchiveProvider } from './MetServiceArchiveProvider.js';
import { OgimetSynopProvider } from './OgimetSynopProvider.js';
import { SynopticProvider } from './SynopticProvider.js';
import {ZephyrProvider} from './ZephyrProvider.js';

const WEATHER_PROXY_URL = "https://awiw6jbmrblqnwsy3qvebx5f240ynebd.lambda-url.ap-southeast-2.on.aws/";

export class WeatherService {

  constructor(providers) {
    this.providers = providers;
  }

  async fetch(start, end, bbox, now = Date.now(), coursePoints = [], forceFetch = false) {
    console.log("fetch ",bbox);
    const margin = 1 * 60 * 60 * 1000;
    const startMargin = start - margin;
    const results = await Promise.allSettled(
      this.providers.map((p, i) =>
        p.fetch(startMargin, end, bbox, now, coursePoints, forceFetch).then(stations => stations.map(station => ({ station, priority: i })))
      )
    );

    results.forEach((r, i) => {
      if (r.status === 'rejected') console.warn(`WeatherService: provider[${i}] failed:`, r.reason);
    });

    if (results.every(r => r.status === 'rejected')) {
      throw new Error('WeatherService: all providers failed');
    }

    const withPriority = results
      .filter(r => r.status === 'fulfilled')
      .flatMap(r => r.value);

    const result = this.#mergeAll(withPriority);
    console.log("Weather service got data: ",result);
    return result;
  }

  async fetchForRace(extent) {
    return this.fetch(extent.startMs, extent.endMs, WeatherService.bboxFromExtent(extent), Date.now(), extent.coursePoints ?? []);
  }

  static bboxFromExtent(extent) {
    const { minLat, maxLat, minLon, maxLon, widthKm, heightKm } = extent;
    const margin = Math.min(Math.max(0.75, Math.max(widthKm, heightKm), 10), 50);
    const DEG_TO_KM = 111.195;
    const midLat = (minLat + maxLat) / 2;
    const dLat = margin / DEG_TO_KM;
    const dLon = margin / (DEG_TO_KM * Math.cos(midLat * Math.PI / 180));
    return {
      minLat: minLat - dLat,
      maxLat: maxLat + dLat,
      minLon: minLon - dLon,
      maxLon: maxLon + dLon,
    };
  }

  #mergeAll(withPriority) {
    const groups = [];
    const assigned = new Set();

    for (let a = 0; a < withPriority.length; a++) {
      if (assigned.has(a)) continue;
      const group = [withPriority[a]];
      assigned.add(a);
      for (let b = a + 1; b < withPriority.length; b++) {
        if (!assigned.has(b) && withPriority[a].station.sameStation(withPriority[b].station)) {
          group.push(withPriority[b]);
          assigned.add(b);
        }
      }
      groups.push(group);
    }

    return groups.map(group => {
      group.sort((a, b) => a.priority - b.priority);
      return group.slice(1).reduce((merged, { station }) => merged.merge(station), group[0].station);
    });
  }
}

export function createDefaultWeatherService() {
  return new WeatherService([
    new ZephyrProvider(WEATHER_PROXY_URL + 'zephyr-data'),
    // new SynopticProvider(WEATHER_PROXY_URL + 'synoptic'),
    new NoaaMetarProvider(WEATHER_PROXY_URL + 'noaa-metar'),
    new OgimetSynopProvider(WEATHER_PROXY_URL + 'ogimet-synop'),
    new MetServiceSynopProvider(WEATHER_PROXY_URL + 'metservice-synop'),
  ]);

  // These providers not currently used, left here for posterity/later
  //new SynopticProvider(WEATHER_PROXY_URL + 'synoptic'),
  //new MetServiceArchiveProvider()
}
